/**
 * useWebSocket — thin consumer hook over the shared WebSocketContext.
 *
 * Components use this exactly as before (same API surface). Internally it
 * delegates to WebSocketContext so there is only ONE WebSocket connection
 * per browser session regardless of how many components call this hook.
 *
 * The hook:
 *   1. Registers the requested channels with the context (ref-counted)
 *   2. Registers event handlers with the context
 *   3. Cleans everything up on unmount
 */

import { useEffect, useRef } from 'react';
import { useWebSocketContext } from '../contexts/WebSocketContext.js';

interface UseWebSocketOptions {
  /**
   * Channels to subscribe to.
   * Example: ["announcements"] or ["assayerai:tenant:X:announcement:Y"]
   */
  channels: string[];

  /**
   * Event handlers keyed by event type.
   */
  onEvent?: Record<string, (data: any) => void>;

  /**
   * Enable automatic React Query invalidation (handled at context level).
   * Kept for API compatibility — value is read from context.
   */
  autoInvalidate?: boolean;

  /**
   * Debug logging — no longer used (logging moved to context level).
   * Kept for API compatibility.
   */
  debug?: boolean;
}

export function useWebSocket(options: UseWebSocketOptions) {
  const { connected, subscribeChannel, unsubscribeChannel, addEventHandler, removeEventHandler } =
    useWebSocketContext();

  const { channels, onEvent } = options;

  // Keep stable refs for channels and handlers so the effects don't
  // re-run on every render when the caller passes inline arrays/objects.
  const channelsRef = useRef<string[]>(channels);
  channelsRef.current = channels;

  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  // Track what we've subscribed / registered so cleanup is accurate.
  const subscribedChannelsRef = useRef<string[]>([]);
  const registeredHandlersRef = useRef<Array<{ channel: string; event: string; handler: (data: any) => void }>>([]);

  // Stable string key so the effect only re-runs when the channel set changes.
  const channelsKey = channels.join(',');

  useEffect(() => {
    const currentChannels = channelsRef.current;
    const currentOnEvent = onEventRef.current;

    // Subscribe channels
    currentChannels.forEach(ch => subscribeChannel(ch));
    subscribedChannelsRef.current = [...currentChannels];

    // Register event handlers against each channel
    const registered: typeof registeredHandlersRef.current = [];
    if (currentOnEvent) {
      currentChannels.forEach(channel => {
        Object.entries(currentOnEvent).forEach(([event, handler]) => {
          addEventHandler(channel, event, handler);
          registered.push({ channel, event, handler });
        });
      });
    }
    registeredHandlersRef.current = registered;

    return () => {
      // Unsubscribe channels
      subscribedChannelsRef.current.forEach(ch => unsubscribeChannel(ch));
      subscribedChannelsRef.current = [];

      // Remove handlers
      registeredHandlersRef.current.forEach(({ channel, event, handler }) => {
        removeEventHandler(channel, event, handler);
      });
      registeredHandlersRef.current = [];
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelsKey, subscribeChannel, unsubscribeChannel, addEventHandler, removeEventHandler]);

  return { connected };
}
